import { useToast } from 'native-base';
import { useNavigateFromHubToPatientPortalLazyQuery } from '../graphQL';
import { getRoute, useNavigate } from '../routes';
import { parseMrn, parseMrnIntoRoute } from '../utils/mrn';
import Sentry from '../utils/sentry';
import { useLaunchExternalRedirect } from '../utils/useLaunchExternalRedirect';

type NavigateToPatientPortalProps = {
  mrn?: string;
  onCompleted?: () => void;
  onError?: (errorType?: string) => void;
  // If true, passes error types to onError instead of navigating
  handleErrorsInCallback?: boolean;
};

export const useNavigateToPatientPortal = (): {
  navigateToPatientPortal: (props?: NavigateToPatientPortalProps) => void;
} => {
  const navigate = useNavigate();
  const toast = useToast();

  const { manuallyRedirect } = useLaunchExternalRedirect({
    preventRefreshAction: true,
  });

  const [queryForPortalLoginUrl] = useNavigateFromHubToPatientPortalLazyQuery();

  const navigateToPatientPortal = ({
    mrn,
    onCompleted,
    onError,
    handleErrorsInCallback = false,
  }: NavigateToPatientPortalProps = {}): void => {
    const onLoginError = (errorOrMessage: Error | string, errorType?: string): void => {
      Sentry.captureException(
        typeof errorOrMessage === 'string' ? new Error(errorOrMessage) : errorOrMessage,
      );

      toast.show({
        description: 'Could not log you into the patient portal.',
      });

      onError?.(errorType);
    };

    void queryForPortalLoginUrl({
      variables: { mrn },
      onError: onLoginError,
      onCompleted: ({ navigateFromHubToPatientPortal3 }) => {
        const responseType = navigateFromHubToPatientPortal3.__typename;

        if (responseType === 'NavigateFromHubToPatientPortalError') {
          const { errorCode } = navigateFromHubToPatientPortal3;

          if (handleErrorsInCallback) {
            // Pass the error type to onError so the caller can decide what to do
            onError?.(errorCode);
            return;
          }

          // Original behavior: navigate to appropriate error page
          if (errorCode === 'VerifyEligibility') {
            navigate(getRoute('therapyEligibility', {}));
            return;
          }

          if (errorCode === 'NoClinicalSelfReferral') {
            navigate(getRoute('therapyNoSelfRefer', {}));
            return;
          }

          onLoginError(
            `NavigateFromHubToPatientPortal3: Invalid state error returned: ${errorCode as string}`,
            errorCode,
          );
          return;
        }

        if (responseType === 'NavigateFromHubToPatientPortalForward') {
          const { externalRedirect } = navigateFromHubToPatientPortal3;

          if (externalRedirect === undefined || externalRedirect === '') {
            onLoginError('NavigateFromHubToPatientPortal3: No external redirect found.');
            return;
          }

          manuallyRedirect(externalRedirect);
          onCompleted?.();
          return;
        }

        if (responseType === 'NavigateFromHubToPatientPortalReauth') {
          const { mrn: reauthMrn } = navigateFromHubToPatientPortal3;

          if (!parseMrn(reauthMrn)) {
            onLoginError(`NavigateFromHubToPatientPortal3: Invalid MRN: ${reauthMrn}`);
            return;
          }

          const { to, toOptions } = parseMrnIntoRoute(reauthMrn);

          navigate(to, { replace: true, ...toOptions });
          onCompleted?.();
          return;
        }

        onLoginError(
          `NavigateFromHubToPatientPortal3: Invalid typename: ${responseType as string}`,
        );
      },
    });
  };

  return { navigateToPatientPortal };
};
